import React from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import AuthButton from '../auth/AuthButton';
import { CoachViewProvider, useCoachView } from '../../contexts/CoachViewContext';

// ── Inner layout — can consume CoachViewContext ───────────────────────────────

const InnerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { isCoachView, viewAsName } = useCoachView();

  return (
    <div className="min-h-screen bg-dark-900">
      <Navigation />

      {/* Persistent coach-view banner — shown on every page when viewing an athlete */}
      {isCoachView && (
        <div className="bg-blue-600/20 border-b border-blue-600/30 px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-blue-400 text-lg">👁️</span>
            <span className="text-blue-300 text-sm font-medium">
              Viewing as:{' '}
              <span className="text-white font-semibold">{viewAsName || 'Athlete'}</span>
              <span className="text-blue-400 ml-2">(read-only)</span>
            </span>
          </div>
          <button
            onClick={() => navigate('/coach')}
            className="px-3 py-1.5 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded text-sm transition-colors"
          >
            ← Back to Coach Dashboard
          </button>
        </div>
      )}

      <main className="container mx-auto px-4 py-6 max-w-7xl">
        {children}
      </main>
    </div>
  );
};

// ── Outer layout — provides CoachViewContext ──────────────────────────────────

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gold-500 mb-4">ShowTracker</h1>
          <p className="text-gray-400 mb-8">Track your bodybuilding competition prep journey</p>
          <AuthButton />
        </div>
      </div>
    );
  }

  return (
    <CoachViewProvider>
      <InnerLayout>{children}</InnerLayout>
    </CoachViewProvider>
  );
};

export default Layout;
